import { useState, useEffect } from 'react'
import { GameEngine } from '../game/GameEngine'

// AGC: Player history interface for tracking battle statistics
export interface PlayerHistory {
  walletAddress: string
  totalBattles: number
  wins: number
  losses: number
  draws: number
  knockouts: number // Wins in 3 or fewer turns
  totalExperience: number
  lastBattleDate: number
  battleHistory: BattleRecord[]
}

// AGC: Individual battle record
export interface BattleRecord {
  id: string
  timestamp: number
  outcome: 'WIN' | 'LOSS' | 'DRAW'
  turns: number
  isKnockout: boolean
  opponentLevel: number
  playerLevel: number
  experienceGained: number
  tokensWon: number
  tokensLost: number
  opponentAddress?: string // AGC: undefined = BOT, address = P2P opponent
  battleType?: 'PVE' | 'PVP' // AGC: Battle type for display
  battleId?: string // AGC: For P2P battles - links to backend log API
  resolutionTxHash?: string // AGC: Transaction hash for battle resolution (on-chain battles)
}

const DEFAULT_BATTLE_TYPE: 'PVE' | 'PVP' = 'PVE'

const normalizeBattleRecord = (record: BattleRecord): BattleRecord => {
  const battleType = record.battleType === 'PVP' ? 'PVP' : DEFAULT_BATTLE_TYPE
  const playerLevel = Number.isFinite(Number(record.playerLevel)) ? Number(record.playerLevel) : 1
  const opponentLevel = Number.isFinite(Number(record.opponentLevel))
    ? Number(record.opponentLevel)
    : playerLevel
  
  // AGC: ONLY recalculate XP if it's not already provided (or is invalid)
  // AGC: This allows backend/on-chain XP values to take precedence
  const needsRecalculation = !Number.isFinite(Number(record.experienceGained)) || Number(record.experienceGained) < 0
  const experienceGained = needsRecalculation
    ? GameEngine.calculateExperienceGain(
        record.outcome,
        opponentLevel,
        playerLevel,
        battleType
      )
    : Number(record.experienceGained)

  return {
    ...record,
    playerLevel,
    opponentLevel,
    battleType,
    opponentAddress: record.opponentAddress,
    experienceGained,
    resolutionTxHash: record.resolutionTxHash // AGC: Preserve resolution transaction hash
  }
}

const normalizeHistory = (history: PlayerHistory): PlayerHistory => {
  const normalizedRecords = history.battleHistory.map(normalizeBattleRecord)
  let wins = 0
  let losses = 0
  let draws = 0
  let knockouts = 0
  let totalExperience = 0

  normalizedRecords.forEach((battle) => {
    if (battle.outcome === 'WIN') {
      wins += 1
      if (battle.isKnockout) {
        knockouts += 1
      }
    } else if (battle.outcome === 'LOSS') {
      losses += 1
    } else if (battle.outcome === 'DRAW') {
      draws += 1
    }

    totalExperience += battle.experienceGained
  })

  return {
    ...history,
    totalBattles: normalizedRecords.length,
    wins,
    losses,
    draws,
    knockouts,
    totalExperience,
    battleHistory: normalizedRecords
  }
}

// AGC: Hook for managing player history and statistics
export const usePlayerHistory = (walletAddress: string) => {
  const [playerHistory, setPlayerHistory] = useState<PlayerHistory | null>(null)
  const [loading, setLoading] = useState(true)

  // AGC: Load player history from localStorage
  const loadPlayerHistory = (): PlayerHistory => {
    if (!walletAddress) {
      return createEmptyHistory('')
    }

    const storageKey = `origami_history_${walletAddress.toLowerCase()}`
    const stored = localStorage.getItem(storageKey)
    
    if (stored) {
      try {
        const data = JSON.parse(stored) as PlayerHistory
        return normalizeHistory(data)
      } catch (error) {
        console.error('Failed to parse stored player history:', error)
        return createEmptyHistory(walletAddress)
      }
    }
    
    return createEmptyHistory(walletAddress)
  }

  // AGC: Create empty history for new player
  const createEmptyHistory = (address: string): PlayerHistory => {
    return {
      walletAddress: address,
      totalBattles: 0,
      wins: 0,
      losses: 0,
      draws: 0,
      knockouts: 0,
      totalExperience: 0,
      lastBattleDate: 0,
      battleHistory: []
    }
  }

  // AGC: Save player history to localStorage
  const savePlayerHistory = (history: PlayerHistory) => {
    if (!history.walletAddress) return

    const storageKey = `origami_history_${history.walletAddress.toLowerCase()}`
    try {
      localStorage.setItem(storageKey, JSON.stringify(history))
    } catch (error) {
      console.error('Failed to save player history:', error)
    }
  }

  // AGC: Add a new battle record
  const addBattleRecord = (record: Omit<BattleRecord, 'id' | 'timestamp'>): PlayerHistory => {
    const currentHistory = loadPlayerHistory()
    const battleId = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    const battleType = record.battleType === 'PVP' ? 'PVP' : DEFAULT_BATTLE_TYPE
    
    // AGC: Use provided XP if available (from backend/on-chain), otherwise calculate locally
    const experienceGained = typeof record.experienceGained === 'number' && record.experienceGained >= 0
      ? record.experienceGained
      : GameEngine.calculateExperienceGain(
          record.outcome,
          record.opponentLevel,
          record.playerLevel,
          battleType
        )
    
    const newBattleRecord: BattleRecord = normalizeBattleRecord({
      ...record,
      battleType,
      experienceGained,
      id: battleId,
      timestamp: Date.now()
    })

    const updatedHistory: PlayerHistory = {
      ...currentHistory,
      totalBattles: currentHistory.totalBattles + 1,
      wins: record.outcome === 'WIN' ? currentHistory.wins + 1 : currentHistory.wins,
      losses: record.outcome === 'LOSS' ? currentHistory.losses + 1 : currentHistory.losses,
      draws: record.outcome === 'DRAW' ? currentHistory.draws + 1 : currentHistory.draws,
      knockouts: record.isKnockout ? currentHistory.knockouts + 1 : currentHistory.knockouts,
      totalExperience: currentHistory.totalExperience + newBattleRecord.experienceGained,
      lastBattleDate: Date.now(),
      battleHistory: [newBattleRecord, ...currentHistory.battleHistory].slice(0, 50) // Keep last 50 battles
    }

    savePlayerHistory(updatedHistory)
    setPlayerHistory(updatedHistory)

    if (typeof window !== 'undefined') {
      // AGC: Mirror XP gain into progression hook
      window.dispatchEvent(
        new CustomEvent('origami:player-progression-updated', {
          detail: {
            walletAddress
          }
        })
      )

      // AGC: Notify other tabs/components that history records changed
      window.dispatchEvent(
        new CustomEvent('origami:player-history-updated', {
          detail: {
            walletAddress
          }
        })
      )
    }

    return updatedHistory
  }

  // AGC: Calculate win rate percentage
  const getWinRate = (): number => {
    if (!playerHistory || playerHistory.totalBattles === 0) return 0
    return Math.round((playerHistory.wins / playerHistory.totalBattles) * 100)
  }

  // AGC: Calculate KO rate percentage
  const getKORate = (): number => {
    if (!playerHistory || playerHistory.wins === 0) return 0
    return Math.round((playerHistory.knockouts / playerHistory.wins) * 100)
  }

  // AGC: Get recent battle streak
  const getBattleStreak = (): { type: 'win' | 'loss' | 'draw' | 'none', count: number } => {
    if (!playerHistory || playerHistory.battleHistory.length === 0) {
      return { type: 'none', count: 0 }
    }

    const recentBattles = playerHistory.battleHistory.slice(0, 10) // Check last 10 battles
    let currentStreak = 1
    const firstOutcome = recentBattles[0].outcome

    for (let i = 1; i < recentBattles.length; i++) {
      if (recentBattles[i].outcome === firstOutcome) {
        currentStreak++
      } else {
        break
      }
    }

    return {
      type: firstOutcome.toLowerCase() as 'win' | 'loss' | 'draw',
      count: currentStreak
    }
  }

  // AGC: Reset player history (for testing)
  const resetPlayerHistory = () => {
    if (!walletAddress) return
    
    const storageKey = `origami_history_${walletAddress.toLowerCase()}`
    localStorage.removeItem(storageKey)
    const newHistory = createEmptyHistory(walletAddress)
    setPlayerHistory(newHistory)
  }

  // AGC: Get battle statistics summary
  const getBattleStats = () => {
    if (!playerHistory) {
      return {
        winRate: 0,
        koRate: 0,
        streak: { type: 'none', count: 0 },
        averageTurnsPerWin: 0,
        totalTokensWon: 0,
        totalTokensLost: 0
      }
    }

    const winBattles = playerHistory.battleHistory.filter(b => b.outcome === 'WIN')
    const totalTurnsInWins = winBattles.reduce((sum, b) => sum + b.turns, 0)
    const totalTokensWon = playerHistory.battleHistory.reduce((sum, b) => sum + b.tokensWon, 0)
    const totalTokensLost = playerHistory.battleHistory.reduce((sum, b) => sum + b.tokensLost, 0)

    return {
      winRate: getWinRate(),
      koRate: getKORate(),
      streak: getBattleStreak(),
      averageTurnsPerWin: winBattles.length > 0 ? Math.round(totalTurnsInWins / winBattles.length * 10) / 10 : 0,
      totalTokensWon,
      totalTokensLost
    }
  }

  // AGC: Load history when wallet address changes
  useEffect(() => {
    if (!walletAddress) {
      setPlayerHistory(null)
      setLoading(false)
      return
    }

    setLoading(true)
    const history = loadPlayerHistory()
    console.log(`📊 Loaded player history for ${walletAddress.slice(0, 10)}:`, {
      totalBattles: history.totalBattles,
      battleHistoryLength: history.battleHistory.length,
      walletAddress: history.walletAddress
    })
    setPlayerHistory(history)
    setLoading(false)

    const walletLower = walletAddress.toLowerCase()
    const storageKey = `origami_history_${walletLower}`

    const refreshHistory = (source: string) => {
      const refreshedHistory = loadPlayerHistory()
      console.log(`🔄 Refreshed player history (${source}):`, {
        battleHistoryLength: refreshedHistory.battleHistory.length
      })
      setPlayerHistory(refreshedHistory)
    }

    const handleStorage = (event: StorageEvent) => {
      if (event.key?.toLowerCase() === storageKey) {
        refreshHistory('storage')
      }
    }

    const handleCustomEvent: EventListener = (event) => {
      const customEvent = event as CustomEvent<{ walletAddress?: string }>
      const updatedWallet = customEvent.detail?.walletAddress
      if (!updatedWallet || updatedWallet.toLowerCase() === walletLower) {
        refreshHistory('event')
      }
    }

    window.addEventListener('storage', handleStorage)
    window.addEventListener('origami:player-history-updated', handleCustomEvent)
    window.addEventListener('origami:player-history-refresh', handleCustomEvent)

    return () => {
      window.removeEventListener('storage', handleStorage)
      window.removeEventListener('origami:player-history-updated', handleCustomEvent)
      window.removeEventListener('origami:player-history-refresh', handleCustomEvent)
    }
  }, [walletAddress])

  return {
    playerHistory,
    loading,
    addBattleRecord,
    getWinRate,
    getKORate,
    getBattleStreak,
    resetPlayerHistory,
    getBattleStats
  }
}

